/**
 * Compose — the new "one prompt, every network" front door.
 *
 *   /portal/content-planner
 *
 * Editor enters: prompt + optional URL + optional reference screenshots.
 * Backend calls Claude with all that context, generates a tailored
 * draft + image per network, returns them, persists each as a planner
 * draft row (status='drafted'). The Compose results pane shows the
 * outputs side-by-side and lets the editor:
 *   · refine in bulk (re-apply across selected drafts)
 *   · open a result → edit inline → save
 *   · push to Buffer
 *   · open the source draft in the lane editor for granular work
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Link as Linkr, Wand2, Globe, Upload, Image as ImageIcon, X, Sparkles, Send, Eye, Download, RefreshCw } from "lucide-react";
import { useAdminToken, studioFetch } from "../components/studio/_shared";
import ContentPlannerLayout from "../components/layout/ContentPlannerLayout";
import { MicField } from "../components/studio/MicField";
import { BufferProvider, useBuffer, channelsForAngle } from "../components/content/BufferContext";
import ImageStudio from "../components/content/ImageStudio";

const API = process.env.REACT_APP_BACKEND_URL;

// Mon/Wed/Fri cadence helpers. The launch sequence publishes on these
// three weekdays — every compose defaults to the next available slot,
// and any user-picked date that already has a planned post triggers a
// cascade-bump (see backend /api/content-calendar/cascade-bump).
const CADENCE_WEEKDAYS = new Set([1, 3, 5]); // JS getDay(): Sun=0, Mon=1, Wed=3, Fri=5

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function nextCadenceISO(from) {
  const d = new Date(from);
  d.setHours(0, 0, 0, 0);
  // Strictly AFTER the given day, find next Mon/Wed/Fri.
  for (let i = 1; i < 8; i += 1) {
    const cur = new Date(d);
    cur.setDate(cur.getDate() + i);
    if (CADENCE_WEEKDAYS.has(cur.getDay())) return toISODate(cur);
  }
  return toISODate(d);
}

const NETWORKS = [
  { key: "instagram",         label: "Instagram",         tone: "#E1306C" },
  { key: "linkedin-text",     label: "LinkedIn (HV Page)", tone: "#0A66C2" },
  { key: "facebook",          label: "Facebook",           tone: "#1877F2" },
];

export default function ComposePage() {
  const token = useAdminToken();
  return (
    <BufferProvider token={token}>
      <ComposeInner token={token} />
    </BufferProvider>
  );
}

function ComposeInner({ token }) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [urlCtx, setUrlCtx] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [scrapeErr, setScrapeErr] = useState("");
  const [refs, setRefs] = useState([]);                  // [{url, note}]
  const [chosen, setChosen] = useState({
    instagram: true, "linkedin-text": true, facebook: true,
  });
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [drafts, setDrafts] = useState([]);              // last run's outputs
  const [sourceSlug, setSourceSlug] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [refineInstr, setRefineInstr] = useState("");
  const [refining, setRefining] = useState(false);
  const [regenImg, setRegenImg] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  // Image studio output — once the editor finishes the Upload→Refine→
  // Brand chrome flow, the final served URL lands here and is attached
  // to every network draft when "Compose" runs.
  const [composedImage, setComposedImage] = useState("");
  const fileRef = useRef(null);

  // ─── Schedule + cascade state ──────────────────────────────────────
  // Default: the next Mon/Wed/Fri after today. Editor can override.
  const defaultDate = nextCadenceISO(new Date());
  const [scheduledFor, setScheduledFor] = useState(defaultDate);
  const [conflict, setConflict] = useState(null);   // { slugs:[...], drafts:[...] }
  const [scanning, setScanning] = useState(false);

  // Re-scan for conflicts every time the picked date changes. We call
  // the cascade-bump endpoint in dry_run mode — it returns the exact
  // chain of sources that will shift (not just direct collisions on the
  // picked date), so the status copy reflects the true downstream blast
  // radius.
  useEffect(() => {
    if (!scheduledFor || !token) { setConflict(null); return; }
    let alive = true;
    setScanning(true);
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/content-calendar/cascade-bump`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ from_date: scheduledFor, dry_run: true }),
          },
        );
        const j = await r.json();
        if (!alive) return;
        const shifted = j.shifted || [];
        if (shifted.length === 0) {
          setConflict(null);
        } else {
          setConflict({
            slugs: shifted.map((s) => s.source_slug),
            shifted,
          });
        }
      } catch (_) { /* noop */ }
      finally { if (alive) setScanning(false); }
    })();
    return () => { alive = false; };
  }, [scheduledFor, token]);

  useEffect(() => {
    if (!token) navigate("/admin/login", { replace: true });
  }, [token, navigate]);

  // ─── Idea → Compose deep-link prefill ─────────────────────────────
  // Ideas board sends ?idea_id=…&title=…&note=…&tags=… when an editor
  // hits "Promote to Compose". We seed the prompt with the title + note
  // so they don't retype, and stash idea_id so the resulting draft can
  // backlink to the originating idea (lets us mark it "fulfilled" later
  // without a separate trip back to Ideas). Runs once on mount only.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const title = params.get("title");
    const note  = params.get("note");
    if (!title && !note) return;
    const stitched = [title, note].filter(Boolean).join("\n\n");
    setPrompt((cur) => (cur && cur.trim() ? cur : stitched));
    // Clean the query string so a refresh doesn't re-seed over a
    // user's later edits — the prefill is one-shot, not sticky.
    window.history.replaceState({}, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrape = async () => {
    if (!urlInput.trim()) return;
    setScraping(true); setScrapeErr("");
    try {
      const d = await studioFetch("/api/compose/scrape-url", {
        method: "POST", token, body: { url: urlInput.trim() },
      });
      setUrlCtx(d);
    } catch (e) { setScrapeErr(e.message); }
    finally { setScraping(false); }
  };

  const uploadRef = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("format", "ig-square");      // any format ok — just stored
    const r = await fetch(`${API}/api/content-calendar/visuals/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    const j = await r.json();
    if (!r.ok) { setError(j.detail || "upload failed"); return; }
    setRefs((p) => [...p, { url: j.url, note: "" }]);
  };

  const run = async () => {
    if (!prompt.trim()) { setError("Write a prompt first."); return; }
    const nets = Object.keys(chosen).filter((k) => chosen[k]);
    if (nets.length === 0) { setError("Pick at least one network."); return; }
    // If the chosen publish date is occupied, confirm the cascade before
    // we burn LLM tokens. The user explicitly opted into the bump
    // behaviour, but a confirm is still polite when N>0 posts move.
    if (conflict && conflict.slugs.length > 0) {
      const lines = conflict.slugs.map((s) => `  • ${s}`).join("\n");
      const ok = window.confirm(
        `Publishing on ${scheduledFor} will push these planned posts forward by one Mon/Wed/Fri slot:\n\n${lines}\n\n` +
        "Any Buffer scheduled times will be rescheduled in lock-step.\n\n" +
        "Proceed?",
      );
      if (!ok) return;
    }
    setRunning(true); setError(""); setDrafts([]); setSelectedIds(new Set());
    try {
      const d = await studioFetch("/api/compose/run", {
        method: "POST", token,
        body: {
          prompt: prompt.trim(),
          networks: nets,
          url_context: urlCtx,
          references: refs.map((r) => ({ url: r.url, note: r.note || "" })),
          // Image studio takes care of imagery now. We only fall back
          // to Gemini per-network generation if the editor explicitly
          // hasn't built one via Image Studio AND has no other photo.
          generate_images: !composedImage,
          attached_image_url: composedImage || null,
          scheduled_for: scheduledFor || null,
        },
      });
      setDrafts(d.drafts || []);
      setSourceSlug(d.source_slug);
      setSelectedIds(new Set(d.draft_ids));

      // Cascade existing posts forward if there was a conflict. The new
      // draft just inserted (d.source_slug) gets excluded from the bump.
      if (conflict && conflict.slugs.length > 0 && scheduledFor) {
        try {
          const c = await studioFetch("/api/content-calendar/cascade-bump", {
            method: "POST", token,
            body: { from_date: scheduledFor, source_slug_to_keep: d.source_slug },
          });
          const bufferFailures = (c.buffer_results || []).filter((r) => !r.ok);
          const moved = (c.shifted || []).length;
          let msg = `✓ Composed. ${moved} planned post${moved === 1 ? "" : "s"} shifted forward.`;
          if (bufferFailures.length > 0) {
            msg += ` ${bufferFailures.length} Buffer reschedule${bufferFailures.length === 1 ? "" : "s"} failed — check Buffer manually.`;
          }
          setError(msg);
          setConflict(null);
        } catch (e) {
          setError(`Composed, but cascade failed: ${e.message}. Posts may overlap in the calendar.`);
        }
      }
    } catch (e) { setError(e.message); }
    finally { setRunning(false); }
  };

  const refine = async () => {
    if (!refineInstr.trim() || selectedIds.size === 0) return;
    setRefining(true); setError("");
    try {
      const d = await studioFetch("/api/compose/refine", {
        method: "POST", token,
        body: {
          instruction: refineInstr.trim(),
          draft_ids: Array.from(selectedIds),
          regenerate_image: regenImg,
        },
      });
      // Merge updates into local drafts state
      setDrafts((prev) => prev.map((row) => {
        const next = d.drafts.find((x) => x.draft_id === row.__id);
        if (!next) return row;
        return { ...row, ...next };
      }));
      setRefineInstr("");
    } catch (e) { setError(e.message); }
    finally { setRefining(false); }
  };

  // Augment drafts with their persisted draft_id so refine can target them
  const draftsWithId = drafts.map((d, i) => ({
    ...d, __id: d.draft_id || d.__id || (sourceSlug && sourceSlug + "-" + i),
  }));

  return (
    <ContentPlannerLayout>
      <Helmet>
        <title>Compose — HidroVerse Content Planner</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div data-testid="compose-page">

        <div className="grid gap-5"
             style={{ gridTemplateColumns: "minmax(0, 480px) minmax(0, 1fr)" }}>
          {/* ─── LEFT — brief builder ───────────────────────────── */}
          <aside className="space-y-4">
            <div className="hv-card-soft" style={{ padding: 16 }}>
              <div className="flex items-center gap-2 mb-2">
                <Wand2 size={14} style={{ color: "#0E7A82" }} />
                <span className="font-mono uppercase"
                      style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                               color: "#0E7A82", fontWeight: 700 }}>
                  The brief
                </span>
              </div>
              <MicField value={prompt} onChange={setPrompt} apiToken={token}
                        testid="compose-prompt-mic">
                <textarea value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && prompt.trim()) {
                              run();
                            }
                          }}
                          rows={7}
                          placeholder={
                            "Tell the AI what you want. Example:\n\n" +
                            "'Borehole-yield drops 30–40% from drilling-day test " +
                            "to long-term sustainable yield. Lodge GMs need to " +
                            "buy against the lower number. Portrait for IG. " +
                            "Highlight the gala-Saturday failure case. Reference " +
                            "SANS 10299-4. Punchy, declarative, no fluff.'"
                          }
                          data-testid="compose-prompt"
                          className="hv-input w-full"
                          style={{ resize: "vertical", fontSize: "0.92rem",
                                   lineHeight: 1.55, minHeight: 160 }} />
              </MicField>
              <div className="mt-2 font-mono uppercase"
                   style={{ fontSize: "0.55rem", letterSpacing: "0.18em",
                            color: "rgba(12,42,64,0.5)" }}>
                ⌘ / Ctrl + Enter to run
              </div>
            </div>

            <div className="hv-card-soft" style={{ padding: 16 }}>
              <div className="flex items-center gap-2 mb-2">
                <Globe size={14} style={{ color: "#0E7A82" }} />
                <span className="font-mono uppercase"
                      style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                               color: "#0E7A82", fontWeight: 700 }}>
                  URL context (optional)
                </span>
              </div>
              <div className="flex gap-2">
                <input type="text" value={urlInput}
                       onChange={(e) => setUrlInput(e.target.value)}
                       placeholder="https://hidroverse.co.za/services/..."
                       data-testid="compose-url"
                       className="hv-input w-full"
                       style={{ fontSize: "0.85rem" }} />
                <button type="button" onClick={scrape}
                        disabled={scraping || !urlInput.trim()}
                        data-testid="compose-url-scrape"
                        className="hv-pill is-active">
                  {scraping ? "Scanning…" : "Scan"}
                </button>
              </div>
              {scrapeErr && (
                <div className="mt-2 font-mono"
                     style={{ fontSize: "0.72rem", color: "#B85C38" }}>
                  {scrapeErr}
                </div>
              )}
              {urlCtx && (
                <div className="mt-3 p-2"
                     data-testid="compose-url-ctx"
                     style={{ background: "rgba(31,182,193,0.08)",
                              border: "1px solid rgba(31,182,193,0.30)",
                              borderRadius: 8, fontSize: "0.78rem",
                              lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 600 }}>{urlCtx.title}</div>
                  <div style={{ color: "rgba(12,42,64,0.65)" }}>
                    {urlCtx.description}
                  </div>
                  <div className="font-mono mt-1"
                       style={{ fontSize: "0.65rem",
                                color: "rgba(12,42,64,0.55)" }}>
                    {urlCtx.body?.length || 0} chars of body captured ·
                    {urlCtx.headings?.length || 0} headings
                  </div>
                </div>
              )}
            </div>

            {/* ─── IMAGE STUDIO — the Canva-replacer ──────────────────
                Upload → refine → brand chrome. Outputs ONE finished
                social-media post and writes it back into the compose
                draft so the publish step picks it up automatically. */}
            <ImageStudio
              token={token}
              prompt={prompt}
              defaultTagline=""
              onUseImage={({ url }) => setComposedImage(url)}
            />
            {composedImage && (
              <div className="hv-card-soft p-3 flex items-center gap-3"
                   data-testid="compose-image-attached"
                   style={{
                     background: "rgba(31,182,193,0.06)",
                     border: "1px solid rgba(31,182,193,0.30)",
                   }}>
                <img src={`${API}${composedImage}`} alt=""
                     style={{ width: 64, height: 64, objectFit: "cover",
                              borderRadius: 6, background: "#16181B" }} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono uppercase"
                       style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                                color: "#0E7A82", fontWeight: 700 }}>
                    Image attached to this compose
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.65)" }}>
                    Will be saved with the draft when you compose.
                  </div>
                </div>
                <button type="button" onClick={() => setComposedImage("")}
                        data-testid="compose-image-clear"
                        className="hv-pill">
                  <X size={11} /> Detach
                </button>
              </div>
            )}

            <div className="hv-card-soft" style={{ padding: 16 }}>
              <div className="flex items-center gap-2 mb-2">
                <ImageIcon size={14} style={{ color: "#0E7A82" }} />
                <span className="font-mono uppercase"
                      style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                               color: "#0E7A82", fontWeight: 700 }}>
                  Reference screenshots ({refs.length})
                </span>
              </div>
              <p className="font-light mb-2"
                 style={{ fontSize: "0.78rem",
                          color: "rgba(12,42,64,0.65)", lineHeight: 1.4 }}>
                Optional. AI uses these as STYLE references for the post
                copy — not as the post image. Use Image studio above for
                the actual image.
              </p>
              <input type="file" accept="image/*" ref={fileRef}
                     onChange={(e) => uploadRef(e.target.files?.[0])}
                     style={{ display: "none" }}
                     data-testid="compose-ref-file" />
              <button type="button"
                      onClick={() => fileRef.current?.click()}
                      data-testid="compose-ref-add"
                      className="hv-pill is-active">
                <Upload size={11} /> Add screenshot
              </button>
              {refs.length > 0 && (
                <ul className="mt-3 grid grid-cols-2 gap-2"
                    data-testid="compose-refs-list">
                  {refs.map((r, i) => (
                    <li key={i}
                        className="relative"
                        style={{ background: "#16181B", borderRadius: 6,
                                 padding: 4 }}>
                      <img src={`${API}${r.url}`} alt=""
                           onClick={() => setLightbox({ url: `${API}${r.url}` })}
                           style={{ width: "100%", height: 90,
                                    objectFit: "contain", cursor: "zoom-in" }} />
                      <input type="text" value={r.note}
                             onChange={(e) => setRefs((p) => p.map((x, j) =>
                               j === i ? { ...x, note: e.target.value } : x))}
                             placeholder="What to focus on?"
                             data-testid={`compose-ref-note-${i}`}
                             className="hv-input w-full mt-1"
                             style={{ fontSize: "0.7rem", padding: "3px 6px" }} />
                      <button type="button"
                              onClick={() => setRefs((p) => p.filter((_, j) => j !== i))}
                              className="hv-pill is-danger"
                              data-testid={`compose-ref-remove-${i}`}
                              style={{ position: "absolute", top: 4, right: 4,
                                       padding: "2px 5px" }}>
                        <X size={10} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="hv-card-soft" style={{ padding: 16 }}>
              <div className="font-mono uppercase mb-2"
                   style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                            color: "#0E7A82", fontWeight: 700 }}>
                Publish date · Mon · Wed · Fri
              </div>
              <input type="date" value={scheduledFor || ""}
                     onChange={(e) => setScheduledFor(e.target.value)}
                     data-testid="compose-schedule-date"
                     className="hv-input w-full"
                     style={{ fontSize: "0.88rem" }} />
              <div className="mt-2 font-mono"
                   style={{ fontSize: "0.7rem",
                            color: scanning ? "rgba(12,42,64,0.55)"
                                 : conflict ? "#B85C38" : "#0E7A82",
                            lineHeight: 1.45 }}
                   data-testid="compose-schedule-status">
                {scanning && "Scanning calendar…"}
                {!scanning && !conflict && scheduledFor &&
                  `Slot is free. New post pins to ${scheduledFor}.`}
                {!scanning && conflict && (() => {
                  const n = conflict.slugs.length;
                  const last = conflict.shifted?.[conflict.shifted.length - 1];
                  const lastDate = last?.new_date;
                  return (
                    <>
                      Conflict on {scheduledFor} — {n} planned source{n === 1 ? "" : "s"} will
                      shift forward by one Mon/Wed/Fri slot{lastDate ? ` (chain ends ${lastDate})` : ""}.
                      Buffer will reschedule in lock-step.
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="hv-card-soft" style={{ padding: 16 }}>
              <div className="font-mono uppercase mb-2"
                   style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                            color: "#0E7A82", fontWeight: 700 }}>
                Networks
              </div>
              <div className="space-y-2">
                {NETWORKS.map((n) => (
                  <label key={n.key} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!chosen[n.key]}
                           onChange={(e) => setChosen((p) => ({ ...p, [n.key]: e.target.checked }))}
                           data-testid={`compose-net-${n.key}`} />
                    <span style={{ fontSize: "0.85rem" }}>
                      <span style={{ color: n.tone, fontWeight: 600 }}>●</span>{" "}
                      {n.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {error && (
              <div className="px-3 py-2"
                   style={{ background: "rgba(184,92,56,0.10)",
                            border: "1px solid rgba(184,92,56,0.35)",
                            color: "#B85C38", fontSize: "0.82rem",
                            borderRadius: 8 }}
                   data-testid="compose-error">
                {error}
              </div>
            )}

            <button type="button" onClick={run}
                    disabled={running || !prompt.trim()}
                    data-testid="compose-run"
                    className="hv-btn w-full justify-center"
                    style={{ fontSize: "0.95rem", padding: "10px 16px" }}>
              <Sparkles size={14} />
              {running ? "Composing every network (30–60s)…" : "Compose all selected networks"}
            </button>
          </aside>

          {/* ─── RIGHT — results ───────────────────────────────── */}
          <section>
            {drafts.length === 0 && !running && (
              <div className="hv-card-soft p-8 text-center" data-testid="compose-empty">
                <Sparkles size={20} style={{ color: "#1FB6C1", margin: "0 auto" }} />
                <h3 className="mt-3" style={{ fontSize: "1rem", fontWeight: 600 }}>
                  Write your brief on the left
                </h3>
                <p className="mt-2 font-light max-w-md mx-auto"
                   style={{ fontSize: "0.88rem",
                            color: "rgba(12,42,64,0.65)", lineHeight: 1.55 }}>
                  We'll generate a tailored post + image for every
                  network you select. Each respects that network's voice
                  rules and native image aspect ratio.
                </p>
              </div>
            )}

            {running && (
              <div className="hv-card-soft p-8 text-center" data-testid="compose-running">
                <div className="font-mono uppercase"
                     style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                              color: "#0E7A82", fontWeight: 700 }}>
                  Generating…
                </div>
                <p className="mt-2 font-light"
                   style={{ fontSize: "0.88rem",
                            color: "rgba(12,42,64,0.65)" }}>
                  Writing each network's text + rendering visuals in
                  parallel. This takes 30–60 seconds.
                </p>
              </div>
            )}

            {drafts.length > 0 && (
              <>
                {/* Refine bar */}
                <div className="hv-card-soft mb-4"
                     style={{ padding: 12, background: "rgba(31,182,193,0.06)",
                              borderColor: "rgba(31,182,193,0.30)" }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={13} style={{ color: "#0E7A82" }} />
                    <span className="font-mono uppercase"
                          style={{ fontSize: "0.6rem", letterSpacing: "0.20em",
                                   color: "#0E7A82", fontWeight: 700 }}>
                      Refine {selectedIds.size === drafts.length ? "all" : `selected (${selectedIds.size})`}
                    </span>
                    <div className="flex-1" />
                    <label className="flex items-center gap-1 font-mono uppercase"
                           style={{ fontSize: "0.55rem", letterSpacing: "0.16em",
                                    color: "rgba(12,42,64,0.65)" }}>
                      <input type="checkbox" checked={regenImg}
                             onChange={(e) => setRegenImg(e.target.checked)}
                             data-testid="compose-regen-img" />
                      Regenerate images too
                    </label>
                  </div>
                  <MicField value={refineInstr} onChange={setRefineInstr}
                            apiToken={token} testid="compose-refine-mic">
                    <textarea value={refineInstr}
                              onChange={(e) => setRefineInstr(e.target.value)}
                              rows={2}
                              placeholder="e.g. 'Punchier — drop the SANS reference, lead with the lodge-failure case'"
                              data-testid="compose-refine"
                              className="hv-input w-full"
                              style={{ resize: "vertical", fontSize: "0.85rem" }} />
                  </MicField>
                  <div className="mt-2 flex gap-2">
                    <button type="button" onClick={refine}
                            disabled={refining || !refineInstr.trim() || selectedIds.size === 0}
                            data-testid="compose-refine-run"
                            className="hv-pill is-active">
                      <Sparkles size={11} />
                      {refining ? "Refining…" : `Apply to ${selectedIds.size}`}
                    </button>
                    {selectedIds.size !== drafts.length && (
                      <button type="button"
                              onClick={() => setSelectedIds(new Set(draftsWithId.map((x) => x.__id)))}
                              data-testid="compose-select-all"
                              className="hv-pill">
                        Select all
                      </button>
                    )}
                  </div>
                </div>

                {/* Result tiles */}
                <div className="grid gap-4"
                     style={{ gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))" }}>
                  {draftsWithId.map((d) => (
                    <ResultTile key={d.__id} draft={d}
                                selected={selectedIds.has(d.__id)}
                                onToggle={() => setSelectedIds((p) => {
                                  const next = new Set(p);
                                  if (next.has(d.__id)) next.delete(d.__id);
                                  else next.add(d.__id);
                                  return next;
                                })}
                                onLightbox={(url) => setLightbox({ url })}
                                token={token} />
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </div>

      {lightbox && (
        <Lightbox url={lightbox.url} onClose={() => setLightbox(null)} />
      )}
    </ContentPlannerLayout>
  );
}

function ResultTile({ draft, selected, onToggle, onLightbox, token }) {
  const { channels } = useBuffer();
  const net = NETWORKS.find((n) => n.key === draft.network) || { tone: "#0E7A82", label: draft.network };
  const imgUrl = draft.suggested_image || draft.image_url;
  const abs = imgUrl
    ? (imgUrl.startsWith("http") ? imgUrl : `${API}${imgUrl}`)
    : null;
  const eligible = channelsForAngle(channels, draft.network);
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(false);
  const [pushErr, setPushErr] = useState("");

  const pushBuffer = async () => {
    if (eligible.length === 0) {
      setPushErr("This network isn't connected in Buffer.");
      return;
    }
    if (!window.confirm(`Push this ${net.label} draft to Buffer's queue?`)) return;
    setPushing(true); setPushErr("");
    try {
      const r = await fetch(`${API}/api/buffer/push-draft`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          draft_id: draft.__id || draft.draft_id,
          channel_id: eligible[0].id,
        }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || j.detail || "Buffer rejected");
      setPushed(true);
    } catch (e) { setPushErr(e.message); }
    finally { setPushing(false); }
  };

  return (
    <article className="hv-card-soft"
             style={{ padding: 14,
                      borderColor: selected ? net.tone : "rgba(12,42,64,0.10)" }}
             data-testid={`compose-result-${draft.network}`}>
      <header className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={selected}
                 onChange={onToggle}
                 data-testid={`compose-result-select-${draft.network}`} />
          <span style={{ color: net.tone, fontWeight: 600 }}>●</span>
          <span className="font-mono uppercase"
                style={{ fontSize: "0.6rem", letterSpacing: "0.20em",
                         color: "#0C2A40", fontWeight: 700 }}>
            {net.label}
          </span>
        </div>
        {abs && (
          <div className="flex gap-1">
            <button type="button" onClick={() => onLightbox(abs)}
                    data-testid={`compose-result-zoom-${draft.network}`}
                    className="hv-pill" title="View full size">
              <Eye size={10} />
            </button>
            <a href={`${abs.replace(/\.png$/, "")}/download`}
               target="_blank" rel="noreferrer"
               data-testid={`compose-result-download-${draft.network}`}
               className="hv-pill"
               style={{ textDecoration: "none" }}
               title="Download">
              <Download size={10} />
            </a>
          </div>
        )}
      </header>
      {abs ? (
        <button type="button"
                onClick={() => onLightbox(abs)}
                style={{ background: "transparent", border: "none",
                         padding: 0, cursor: "zoom-in", width: "100%" }}>
          <img src={abs} alt="" loading="lazy"
               style={{ width: "100%", height: 220, objectFit: "contain",
                        background: "#16181B", borderRadius: 6 }} />
        </button>
      ) : (
        <div style={{ height: 80, background: "rgba(12,42,64,0.05)",
                      borderRadius: 6, display: "flex",
                      alignItems: "center", justifyContent: "center",
                      fontSize: "0.78rem",
                      color: "rgba(12,42,64,0.55)" }}>
          (No image generated)
        </div>
      )}
      <div className="mt-2"
           style={{ fontSize: "0.88rem", fontWeight: 600,
                    lineHeight: 1.35 }}>
        {draft.hook}
      </div>
      <p className="mt-1 font-light"
         style={{ fontSize: "0.82rem", color: "rgba(12,42,64,0.75)",
                  lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
        {draft.body}
      </p>
      {draft.cta && (
        <div className="mt-2 font-mono uppercase"
             style={{ fontSize: "0.65rem", letterSpacing: "0.16em",
                      color: "#0E7A82", fontWeight: 700 }}>
          → {draft.cta}
        </div>
      )}
      {Array.isArray(draft.hashtags) && draft.hashtags.length > 0 && (
        <div className="mt-2 font-mono"
             style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.65)",
                      lineHeight: 1.5 }}>
          {draft.hashtags.join(" ")}
        </div>
      )}
      <footer className="mt-3 flex items-center gap-2 flex-wrap">
        <Linkr to={`/portal/content-calendar/plan/compose/${draft.__id || ""}`}
               target="_blank"
               className="hv-pill"
               style={{ textDecoration: "none" }}>
          Open lane editor →
        </Linkr>
        <div className="flex-1" />
        {pushed ? (
          <span className="font-mono uppercase"
                style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                         color: "#0E7A82", fontWeight: 700 }}>
            ✓ Pushed to Buffer
          </span>
        ) : (
          <button type="button" onClick={pushBuffer}
                  disabled={pushing}
                  data-testid={`compose-result-push-${draft.network}`}
                  className="hv-pill is-active"
                  style={{ background: "#0E7A82", color: "#FFFFFF",
                           borderColor: "#0E7A82" }}>
            <Send size={10} /> {pushing ? "Pushing…" : "Push to Buffer"}
          </button>
        )}
      </footer>
      {pushErr && (
        <div className="mt-1 font-mono"
             style={{ fontSize: "0.7rem", color: "#B85C38" }}>
          {pushErr}
        </div>
      )}
    </article>
  );
}

function Lightbox({ url, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div onClick={onClose}
         data-testid="lightbox"
         style={{ position: "fixed", inset: 0, zIndex: 100,
                  background: "rgba(0,0,0,0.85)",
                  display: "flex", alignItems: "center",
                  justifyContent: "center", padding: 32,
                  cursor: "zoom-out" }}>
      <img src={url} alt=""
           onClick={(e) => e.stopPropagation()}
           style={{ maxWidth: "100%", maxHeight: "100%",
                    boxShadow: "0 30px 60px rgba(0,0,0,0.6)",
                    background: "#16181B", cursor: "default" }} />
      <button type="button" onClick={onClose}
              data-testid="lightbox-close"
              style={{ position: "absolute", top: 16, right: 16,
                       background: "rgba(255,255,255,0.10)",
                       color: "#FFFFFF", border: "1px solid rgba(255,255,255,0.30)",
                       borderRadius: 999, padding: "8px 12px",
                       fontFamily: "ui-monospace, monospace",
                       fontSize: "0.7rem", letterSpacing: "0.2em",
                       textTransform: "uppercase", cursor: "pointer" }}>
        <X size={14} /> Close
      </button>
      <a href={`${url.replace(/\.png$/, "")}/download`}
         target="_blank" rel="noreferrer"
         data-testid="lightbox-download"
         style={{ position: "absolute", top: 16, right: 100,
                  background: "rgba(255,255,255,0.10)",
                  color: "#FFFFFF",
                  border: "1px solid rgba(255,255,255,0.30)",
                  borderRadius: 999, padding: "8px 12px",
                  fontFamily: "ui-monospace, monospace",
                  fontSize: "0.7rem", letterSpacing: "0.2em",
                  textTransform: "uppercase", textDecoration: "none" }}>
        <Download size={14} /> Download
      </a>
    </div>
  );
}
